import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Alert, FlatList, Modal, Platform } from 'react-native';
import { Text, TextInput, Button, Card, Divider, ActivityIndicator, Searchbar, FAB, Menu } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import api from '../../services/api';

const NuevaCompraScreen = () => {
  const navigation = useNavigation();

  // Proveedor
  const [proveedores, setProveedores] = useState([]);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [proveedorSearch, setProveedorSearch] = useState('');

  // Productos
  const [productos, setProductos] = useState([]);
  const [items, setItems] = useState([]);
  const [showProductoModal, setShowProductoModal] = useState(false);
  const [productoSearch, setProductoSearch] = useState('');

  // Datos de la compra
  const [facturaProveedor, setFacturaProveedor] = useState('');
  const [fechaEntrega, setFechaEntrega] = useState('');
  const [fechaEntregaDate, setFechaEntregaDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [notas, setNotas] = useState('');
  const [impuesto, setImpuesto] = useState('');
  const [descuento, setDescuento] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [presentacionMenuIndex, setPresentacionMenuIndex] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [provData, prodData] = await Promise.all([
        api.getProveedores({ estado: 'activo' }),
        api.getProductos()
      ]);
      setProveedores(provData);
      setProductos(Array.isArray(prodData) ? prodData : (prodData.productos || []));
    } catch (error) {
      console.error('Error al cargar datos:', error);
      Alert.alert('Error', 'No se pudieron cargar los datos');
    } finally {
      setLoadingData(false);
    }
  };

  const agregarProducto = (producto) => {
    // Verificar si ya está agregado
    if (items.find(i => i.producto_id === producto.id)) {
      Alert.alert('Aviso', 'Este producto ya está en la lista');
      return;
    }

    setItems([...items, {
      producto_id: producto.id,
      producto_nombre: producto.nombre,
      producto_codigo: producto.codigo,
      marca: producto.marca,
      cantidad: '1',
      precio_unitario: (producto.precio_compra || producto.precio_venta || 0).toString(),
      presentacion_id: null,
      factor_conversion: 1,
      unidad_nombre: 'Unidad',
      presentaciones: Array.isArray(producto.presentaciones) ? producto.presentaciones : [],
      precio_base: producto.precio_compra || producto.precio_venta || 0,
    }]);
    setShowProductoModal(false);
    setProductoSearch('');
  };

  const actualizarItem = (index, campo, valor) => {
    const newItems = [...items];
    newItems[index][campo] = valor;
    setItems(newItems);
  };

  const cambiarPresentacion = (index, pp) => {
    const newItems = [...items];
    const item = newItems[index];
    const factor = pp ? Number(pp.factor) : 1;
    const nuevoPrecio = Math.round((Number(item.precio_base) || 0) * factor * 100) / 100;
    newItems[index] = {
      ...item,
      cantidad: '1',
      precio_unitario: nuevoPrecio.toString(),
      factor_conversion: factor,
      unidad_nombre: pp ? pp.nombre : 'Unidad',
      presentacion_id: pp ? pp.presentacion_id : null,
    };
    setItems(newItems);
    setPresentacionMenuIndex(null);
  };

  const eliminarItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calcularSubtotal = () => {
    return items.reduce((sum, item) => {
      const precio = parseFloat(item.precio_unitario) || 0;
      const cantidad = parseInt(item.cantidad) || 0;
      return sum + (precio * cantidad);
    }, 0);
  };

  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    const imp = parseFloat(impuesto) || 0;
    const desc = parseFloat(descuento) || 0;
    return subtotal + imp - desc;
  };

  const guardarCompra = async () => {
    if (!proveedorSeleccionado) {
      Alert.alert('Error', 'Selecciona un proveedor');
      return;
    }
    if (items.length === 0) {
      Alert.alert('Error', 'Agrega al menos un producto');
      return;
    }

    // Validar cantidades
    for (const item of items) {
      if (!parseInt(item.cantidad) || parseInt(item.cantidad) <= 0) {
        Alert.alert('Error', `La cantidad de "${item.producto_nombre}" debe ser mayor a 0`);
        return;
      }
      if (!parseFloat(item.precio_unitario) || parseFloat(item.precio_unitario) <= 0) {
        Alert.alert('Error', `El precio de "${item.producto_nombre}" debe ser mayor a 0`);
        return;
      }
    }

    try {
      setSaving(true);
      const data = {
        proveedor_id: proveedorSeleccionado.id,
        items: items.map(item => {
          const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
          return {
            producto_id: item.producto_id,
            cantidad: parseInt(item.cantidad) * factor,
            precio_unitario: Math.round((parseFloat(item.precio_unitario) / factor) * 100) / 100,
            presentacion_id: item.presentacion_id || null,
            factor_conversion: factor,
            unidad_nombre: item.unidad_nombre || 'Unidad',
          };
        }),
        factura_proveedor: facturaProveedor || null,
        fecha_entrega_esperada: fechaEntrega || null,
        notas: notas || null,
        impuesto: parseFloat(impuesto) || 0,
        descuento: parseFloat(descuento) || 0,
      };

      const result = await api.createCompra(data);
      Alert.alert('Orden Creada', result.message || `OC ${result.numero_compra} creada`, [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      console.error('Error al crear compra:', error);
      Alert.alert('Error', error.message || 'Error al crear la orden de compra');
    } finally {
      setSaving(false);
    }
  };

  const filteredProveedores = proveedores.filter(p =>
    (p.nombre || '').toLowerCase().includes(proveedorSearch.toLowerCase()) ||
    (p.contacto || '').toLowerCase().includes(proveedorSearch.toLowerCase())
  );

  const filteredProductos = productos.filter(p =>
    (p.nombre || '').toLowerCase().includes(productoSearch.toLowerCase()) ||
    (p.codigo || '').toLowerCase().includes(productoSearch.toLowerCase()) ||
    (p.marca || '').toLowerCase().includes(productoSearch.toLowerCase())
  );

  if (loadingData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando datos...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Proveedor */}
        <Card style={styles.sectionCard}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="business" size={18} color="#007BFF" /> Proveedor
            </Text>
            <TouchableOpacity
              style={styles.selectorButton}
              onPress={() => setShowProveedorModal(true)}
            >
              {proveedorSeleccionado ? (
                <View>
                  <Text style={styles.selectedName}>{proveedorSeleccionado.nombre}</Text>
                  {proveedorSeleccionado.telefono && (
                    <Text style={styles.selectedDetail}>{proveedorSeleccionado.telefono}</Text>
                  )}
                </View>
              ) : (
                <Text style={styles.placeholderText}>Seleccionar proveedor...</Text>
              )}
              <Ionicons name="chevron-down" size={20} color="#666" />
            </TouchableOpacity>
          </Card.Content>
        </Card>

        {/* Datos adicionales */}
        <Card style={styles.sectionCard}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="document-text" size={18} color="#007BFF" /> Datos de la Compra
            </Text>
            <TextInput
              label="No. Factura del Proveedor"
              value={facturaProveedor}
              onChangeText={setFacturaProveedor}
              mode="outlined"
              style={styles.input}
              dense
            />
            <View style={styles.datePickerButton}>
              <TouchableOpacity
                style={styles.datePickerInner}
                onPress={() => setShowDatePicker(true)}
                activeOpacity={0.7}
              >
                <Ionicons name="calendar-outline" size={20} color="#007BFF" />
                <Text style={[styles.datePickerText, !fechaEntrega && styles.datePickerPlaceholder]}>
                  {fechaEntrega || 'Fecha de Entrega Esperada'}
                </Text>
              </TouchableOpacity>
              {fechaEntrega ? (
                <TouchableOpacity
                  onPress={() => { setFechaEntrega(''); setFechaEntregaDate(null); }}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  style={styles.dateClearBtn}
                >
                  <Ionicons name="close-circle" size={20} color="#DC3545" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity onPress={() => setShowDatePicker(true)} style={styles.dateClearBtn}>
                  <Ionicons name="chevron-down" size={18} color="#888" />
                </TouchableOpacity>
              )}
            </View>
            {showDatePicker && (
              <DateTimePicker
                value={fechaEntregaDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                minimumDate={new Date()}
                onChange={(event, selectedDate) => {
                  setShowDatePicker(Platform.OS === 'ios');
                  if (selectedDate) {
                    setFechaEntregaDate(selectedDate);
                    const y = selectedDate.getFullYear();
                    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const d = String(selectedDate.getDate()).padStart(2, '0');
                    setFechaEntrega(`${y}-${m}-${d}`);
                  }
                }}
              />
            )}
            <TextInput
              label="Notas"
              value={notas}
              onChangeText={setNotas}
              mode="outlined"
              style={styles.input}
              dense
              multiline
              numberOfLines={2}
            />
          </Card.Content>
        </Card>

        {/* Productos */}
        <Card style={styles.sectionCard}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                <Ionicons name="cube" size={18} color="#007BFF" /> Productos ({items.length})
              </Text>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => setShowProductoModal(true)}
              >
                <Ionicons name="add-circle" size={24} color="#007BFF" />
                <Text style={styles.addButtonText}>Agregar</Text>
              </TouchableOpacity>
            </View>

            {items.length === 0 ? (
              <View style={styles.emptyItems}>
                <Ionicons name="cube-outline" size={40} color="#ccc" />
                <Text style={styles.emptyItemsText}>Agrega productos a la orden</Text>
              </View>
            ) : (
              items.map((item, index) => (
                <View key={index} style={styles.itemRow}>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName} numberOfLines={1}>{item.producto_nombre}</Text>
                    {item.producto_codigo && (
                      <Text style={styles.itemCode}>{item.producto_codigo} {item.marca ? `| ${item.marca}` : ''}</Text>
                    )}
                    {Array.isArray(item.presentaciones) && item.presentaciones.length > 0 && (
                      <View style={styles.presentacionRow}>
                        <Text style={styles.presentacionLabel}>Pres:</Text>
                        <Menu
                          visible={presentacionMenuIndex === index}
                          onDismiss={() => setPresentacionMenuIndex(null)}
                          anchor={
                            <TouchableOpacity
                              style={styles.presentacionChip}
                              onPress={() => setPresentacionMenuIndex(index)}
                            >
                              <Text style={styles.presentacionChipText}>{item.unidad_nombre || 'Unidad'} ▾</Text>
                            </TouchableOpacity>
                          }
                        >
                          <Menu.Item title="Unidad (x1)" onPress={() => cambiarPresentacion(index, null)} />
                          {item.presentaciones.map((pp) => (
                            <Menu.Item
                              key={pp.id}
                              title={`${pp.nombre} (x${Number(pp.factor_conversion)})`}
                              onPress={() => cambiarPresentacion(index, {
                                presentacion_id: pp.presentacion_id,
                                nombre: pp.nombre,
                                factor: Number(pp.factor_conversion),
                              })}
                            />
                          ))}
                        </Menu>
                      </View>
                    )}
                    <View style={styles.itemInputs}>
                      <TextInput
                        label="Cant."
                        value={item.cantidad}
                        onChangeText={(v) => actualizarItem(index, 'cantidad', v)}
                        mode="outlined"
                        keyboardType="numeric"
                        style={styles.cantInput}
                        dense
                      />
                      <TextInput
                        label="Precio Unit."
                        value={item.precio_unitario}
                        onChangeText={(v) => actualizarItem(index, 'precio_unitario', v)}
                        mode="outlined"
                        keyboardType="decimal-pad"
                        style={styles.precioInput}
                        dense
                      />
                      <Text style={styles.itemSubtotal}>
                        L. {((parseFloat(item.precio_unitario) || 0) * (parseInt(item.cantidad) || 0)).toFixed(2)}
                      </Text>
                    </View>
                  </View>
                  <TouchableOpacity onPress={() => eliminarItem(index)} style={styles.deleteButton}>
                    <Ionicons name="trash-outline" size={20} color="#DC3545" />
                  </TouchableOpacity>
                </View>
              ))
            )}
          </Card.Content>
        </Card>

        {/* Totales */}
        {items.length > 0 && (
          <Card style={styles.sectionCard}>
            <Card.Content>
              <Text style={styles.sectionTitle}>
                <Ionicons name="calculator" size={18} color="#007BFF" /> Totales
              </Text>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal:</Text>
                <Text style={styles.totalValue}>L. {calcularSubtotal().toFixed(2)}</Text>
              </View>
              <View style={styles.totalInputRow}>
                <TextInput
                  label="Impuesto (L.)"
                  value={impuesto}
                  onChangeText={setImpuesto}
                  mode="outlined"
                  keyboardType="decimal-pad"
                  style={styles.totalInput}
                  dense
                />
                <TextInput
                  label="Descuento (L.)"
                  value={descuento}
                  onChangeText={setDescuento}
                  mode="outlined"
                  keyboardType="decimal-pad"
                  style={styles.totalInput}
                  dense
                />
              </View>
              <Divider style={{ marginVertical: 8 }} />
              <View style={styles.totalRow}>
                <Text style={styles.grandTotalLabel}>TOTAL:</Text>
                <Text style={styles.grandTotalValue}>L. {calcularTotal().toFixed(2)}</Text>
              </View>
            </Card.Content>
          </Card>
        )}

        {/* Botones */}
        <View style={styles.buttonsContainer}>
          <Button
            mode="contained"
            onPress={guardarCompra}
            loading={saving}
            disabled={saving || items.length === 0 || !proveedorSeleccionado}
            style={styles.saveButton}
            icon="content-save"
          >
            Crear Orden de Compra
          </Button>
          <Button
            mode="outlined"
            onPress={() => navigation.goBack()}
            style={styles.cancelButton}
          >
            Cancelar
          </Button>
        </View>
      </ScrollView>

      {/* Modal Proveedor */}
      <Modal visible={showProveedorModal} animationType="slide" onRequestClose={() => setShowProveedorModal(false)}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Seleccionar Proveedor</Text>
            <TouchableOpacity onPress={() => setShowProveedorModal(false)}>
              <Ionicons name="close" size={28} color="#333" />
            </TouchableOpacity>
          </View>
          <Searchbar
            placeholder="Buscar proveedor..."
            value={proveedorSearch}
            onChangeText={setProveedorSearch}
            style={styles.modalSearch}
          />
          <FlatList
            data={filteredProveedores}
            keyExtractor={(item) => item.id.toString()}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.modalItem}
                onPress={() => { setProveedorSeleccionado(item); setShowProveedorModal(false); setProveedorSearch(''); }}
              >
                <View>
                  <Text style={styles.modalItemName}>{item.nombre}</Text>
                  <Text style={styles.modalItemDetail}>
                    {[item.contacto, item.telefono].filter(Boolean).join(' | ') || 'Sin contacto'}
                  </Text>
                </View>
                {proveedorSeleccionado?.id === item.id && (
                  <Ionicons name="checkmark-circle" size={24} color="#28A745" />
                )}
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.modalEmpty}>
                <Text style={styles.modalEmptyText}>No se encontraron proveedores</Text>
              </View>
            }
          />
        </View>
      </Modal>

      {/* Modal Producto */}
      <Modal visible={showProductoModal} animationType="slide" onRequestClose={() => setShowProductoModal(false)}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Agregar Producto</Text>
            <TouchableOpacity onPress={() => setShowProductoModal(false)}>
              <Ionicons name="close" size={28} color="#333" />
            </TouchableOpacity>
          </View>
          <Searchbar
            placeholder="Buscar producto..."
            value={productoSearch}
            onChangeText={setProductoSearch}
            style={styles.modalSearch}
          />
          <FlatList
            data={filteredProductos}
            keyExtractor={(item) => item.id.toString()}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.modalItem}
                onPress={() => agregarProducto(item)}
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.modalItemName}>{item.nombre}</Text>
                  <Text style={styles.modalItemDetail}>
                    {item.codigo || 'Sin codigo'} {item.marca ? `| ${item.marca}` : ''} | Stock: {item.stock_total || item.stock || 0}
                  </Text>
                </View>
                <Text style={styles.modalItemPrice}>L. {parseFloat(item.precio_compra || item.precio_venta || 0).toFixed(2)}</Text>
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.modalEmpty}>
                <Text style={styles.modalEmptyText}>No se encontraron productos</Text>
              </View>
            }
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  scrollView: { flex: 1 },
  scrollContent: { padding: 8, paddingBottom: 30 },
  sectionCard: { marginBottom: 10, elevation: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 10 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  selectorButton: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 12, backgroundColor: '#F8F9FA', borderRadius: 8, borderWidth: 1, borderColor: '#DEE2E6' },
  selectedName: { fontSize: 15, fontWeight: 'bold', color: '#333' },
  selectedDetail: { fontSize: 13, color: '#666', marginTop: 2 },
  placeholderText: { fontSize: 14, color: '#999' },
  input: { marginBottom: 8, backgroundColor: '#fff' },
  datePickerButton: { flexDirection: 'row', alignItems: 'center', padding: 4, backgroundColor: '#fff', borderRadius: 4, borderWidth: 1, borderColor: '#86b7fe', marginBottom: 8 },
  datePickerInner: { flex: 1, flexDirection: 'row', alignItems: 'center', padding: 8, gap: 8 },
  datePickerText: { flex: 1, fontSize: 14, color: '#333' },
  datePickerPlaceholder: { color: '#999' },
  dateClearBtn: { padding: 8 },
  addButton: { flexDirection: 'row', alignItems: 'center' },
  addButtonText: { color: '#007BFF', marginLeft: 4, fontWeight: 'bold' },
  emptyItems: { alignItems: 'center', paddingVertical: 20 },
  emptyItemsText: { color: '#999', marginTop: 8 },
  itemRow: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#EEE' },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 14, fontWeight: 'bold', color: '#333' },
  itemCode: { fontSize: 12, color: '#888', marginBottom: 4 },
  presentacionRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2, marginBottom: 4 },
  presentacionLabel: { fontSize: 12, color: '#666', marginRight: 6 },
  presentacionChip: { borderWidth: 1, borderColor: '#0066cc', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12, backgroundColor: '#E7F1FF' },
  presentacionChipText: { fontSize: 12, color: '#0066cc', fontWeight: '600' },
  itemInputs: { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  cantInput: { width: 70, marginRight: 8, backgroundColor: '#fff' },
  precioInput: { width: 110, marginRight: 8, backgroundColor: '#fff' },
  itemSubtotal: { fontSize: 14, fontWeight: 'bold', color: '#28A745' },
  deleteButton: { padding: 8, marginTop: 4 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 },
  totalLabel: { fontSize: 14, color: '#666' },
  totalValue: { fontSize: 14, fontWeight: '600', color: '#333' },
  totalInputRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  totalInput: { flex: 1, marginHorizontal: 4, backgroundColor: '#fff' },
  grandTotalLabel: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  grandTotalValue: { fontSize: 18, fontWeight: 'bold', color: '#28A745' },
  buttonsContainer: { padding: 8, marginTop: 4, marginBottom: 20 },
  saveButton: { backgroundColor: '#007BFF', marginBottom: 8 },
  cancelButton: { borderColor: '#6C757D' },
  // Modal
  modalContainer: { flex: 1, backgroundColor: '#fff' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#EEE' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  modalSearch: { margin: 8, elevation: 1 },
  modalItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 14, borderBottomWidth: 1, borderBottomColor: '#F0F0F0' },
  modalItemName: { fontSize: 15, fontWeight: '600', color: '#333' },
  modalItemDetail: { fontSize: 13, color: '#888', marginTop: 2 },
  modalItemPrice: { fontSize: 14, fontWeight: 'bold', color: '#28A745' },
  modalEmpty: { alignItems: 'center', paddingVertical: 40 },
  modalEmptyText: { color: '#999', fontSize: 15 },
});

export default NuevaCompraScreen;
